import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ShieldCheck, ArrowRight, Loader2, RotateCw } from 'lucide-react';

/** Secondo passo dell'accesso da un dispositivo nuovo: il codice arrivato via email. */
export const DeviceCodeStep = ({ challenge, rememberMe, onCancel }) => {
  const { verifyDevice, resendDeviceCode } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyDevice(challenge.challenge_id, code, rememberMe);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verifica non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(''); setInfo(''); setResending(true);
    try {
      await resendDeviceCode(challenge.challenge_id);
      setInfo('Nuovo codice inviato.');
      setCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invio non riuscito');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="device-code-step">
      <div className="space-y-2">
        <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </span>
        <h2 className="text-2xl md:text-3xl font-bold font-outfit">Controlla la tua email</h2>
        <p className="text-muted-foreground">
          È la prima volta che entri da questo dispositivo ({challenge.device_label}). Abbiamo mandato un
          codice a <span className="font-medium text-foreground">{challenge.email_hint}</span>:
          scade tra {challenge.expires_in_minutes} minuti.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="device-code">Codice di verifica</Label>
          <Input
            id="device-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="h-12 text-center text-2xl tracking-[0.4em] font-semibold"
            autoFocus
            data-testid="device-code-input"
          />
        </div>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md" data-testid="device-code-error">
            {error}
          </p>
        )}
        {info && (
          <p className="text-sm text-primary bg-primary/10 p-3 rounded-md" data-testid="device-code-info">{info}</p>
        )}

        <Button type="submit" className="w-full h-11" disabled={busy || code.length < 6}
          data-testid="verify-device-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Conferma e accedi
          {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>

      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={resend} disabled={resending}
          className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5"
          data-testid="resend-code-btn">
          <RotateCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
          Non è arrivato? Rimandalo
        </button>
        <button type="button" onClick={onCancel}
          className="text-muted-foreground hover:text-primary transition-colors" data-testid="cancel-device-btn">
          Cambia account
        </button>
      </div>
    </div>
  );
};

export default DeviceCodeStep;
